import React, { useState, useEffect, useContext } from 'react';
import { Box, Stack, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { HomeContext } from '../utils/contexts/homeContext';
import {
    GetDayJSValueFromUTCDate, GetDateFromDayJSValue, PatientCompleteAPIUrl, GetNoToCurrencyFormat, GetNumberFromCurrencyFormat
} from '../utils/extensions/functionEx';
import dayjs from 'dayjs';
import { useCookies } from 'react-cookie';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
    '& .MuiDialog-paper': {
        maxWidth: '640px'
    }
}));

const CompleteServiceForm = (props) => {
    const { selectedPatientService, handleCloseClick } = props;
    const { ShowAPIError, OpenErrorAlert, OpenSuccessAlert, homeState, ClearLoggedUser } = useContext(HomeContext);

    const [cleared, setCleared] = useState(false);
    const [notes, setNotes] = React.useState(selectedPatientService.service_remarks);
    const [serviceCompletedDate, setServiceCompletedDate] = useState(dayjs());
    const [serviceCompletedDutyDays, setServiceCompletedDutyDays] = useState(0);
    const [nurseCompletedDutyDays, setNurseCompletedDutyDays] = useState(0);
    const [serviceExtendedDutyDays, setServiceExtendedDutyDays] = useState(0);
    const [serviceExtendedDutyDaysFee, setServiceExtendedDutyDaysFee] = useState(0);
    const [serviceExtDutyNurseSalary, setServiceExtDutyNurseSalary] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);

            return () => clearTimeout(timeout);
        }
        return () => { };
    }, [cleared]);

    useEffect(() => {
        let tempNurseCompletedDutyDays = 0;
        let tempServiceCompletedDutyDays = 0;
        let tempServiceExtendedDutyDays = 0;
        let tempStartDate = dayjs();
        let fromDate = dayjs();
        let toDate = dayjs();

        if (selectedPatientService.service_start_date && serviceCompletedDate) {
            tempStartDate = GetDayJSValueFromUTCDate(selectedPatientService.service_start_date);
            fromDate = dayjs(tempStartDate.year().toString() + '-' + (tempStartDate.month() + 1).toString() + '-' + tempStartDate.date().toString());
            toDate = dayjs(serviceCompletedDate.year().toString() + '-' + (serviceCompletedDate.month() + 1).toString() + '-' +
                serviceCompletedDate.date().toString());

            tempServiceCompletedDutyDays = toDate.diff(fromDate, 'day') + 1;
        }

        if (selectedPatientService.service_duty_days < tempServiceCompletedDutyDays) {
            tempServiceExtendedDutyDays = tempServiceCompletedDutyDays - selectedPatientService.service_duty_days;
        }
        if (selectedPatientService.nurse_start_date && serviceCompletedDate) {
            tempStartDate = GetDayJSValueFromUTCDate(selectedPatientService.nurse_start_date);
            fromDate = dayjs(tempStartDate.year().toString() + '-' + (tempStartDate.month() + 1).toString() + '-' + tempStartDate.date().toString());
            toDate = dayjs(serviceCompletedDate.year().toString() + '-' + (serviceCompletedDate.month() + 1).toString() + '-' +
                serviceCompletedDate.date().toString());

            tempNurseCompletedDutyDays = toDate.diff(fromDate, 'day') + 1;
        }
        setServiceExtendedDutyDays(tempServiceExtendedDutyDays);
        setServiceCompletedDutyDays(tempServiceCompletedDutyDays);
        setNurseCompletedDutyDays(tempNurseCompletedDutyDays);
    }, [serviceCompletedDate]);

    const handleClose = () => {
        handleCloseClick(false);
    };

    const handleSaveClick = async () => {
        try {
            if (!selectedPatientService || !serviceCompletedDate) {
                OpenErrorAlert('Enter valid completed date.')
                return;
            }
            if (!serviceCompletedDutyDays || serviceCompletedDutyDays.toString().trim().length === 0 || serviceCompletedDutyDays <= 0) {
                OpenErrorAlert('Enter valid completed date.')
                return;
            }
            setIsLoading(true);
            const tempPatient = {
                service_id: selectedPatientService.service_id,
                service_nurse_id: selectedPatientService.service_nurse_id,
                service_completed_days: serviceCompletedDutyDays,
                nurse_completed_days: nurseCompletedDutyDays,
                extended_duty_days: serviceExtendedDutyDays,
                extended_duty_days_fee: GetNumberFromCurrencyFormat(serviceExtendedDutyDaysFee),
                extended_duty_days_nurse_salary: GetNumberFromCurrencyFormat(serviceExtDutyNurseSalary),
                completed_date: GetDateFromDayJSValue(serviceCompletedDate),
                remarks: notes.trim()
            };
            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(PatientCompleteAPIUrl(homeState.loggedUser.companyCode), {
                method: "PUT",
                headers: headers,
                body: JSON.stringify(tempPatient)
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                OpenSuccessAlert('Patient service closed successfully.');
                setIsLoading(false);
                handleCloseClick(true, selectedPatientService.service_id, selectedPatientService.service_nurse_id);

                return;
            }
            ShowAPIError(response);
            return null;
        }
        catch (error) {
            OpenErrorAlert(error)
            return null;
        }
        finally {
            setIsLoading(false);
        }
    };

    return (
        <React.Fragment>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en-in"}>
                <BootstrapDialog
                    onClose={handleClose}
                    open={props.isOpen}
                >
                    <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                        Complete Patient Service
                    </DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={(theme) => ({
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent dividers>
                        <Stack>
                            <Box>
                                <Grid container spacing={2}>
                                    <Grid size={4}>
                                        <DatePicker format="DD/MM/YYYY"
                                            label="Completed Date" maxDate={dayjs()}
                                            minDate={dayjs().add(-3, 'day')}
                                            value={serviceCompletedDate}
                                            onChange={(newValue) => { setServiceCompletedDate(newValue); }}
                                            slotProps={{
                                                textField: {
                                                    size: "small", fullWidth: "true", required: "true",
                                                    error: !serviceCompletedDate ? true : false,
                                                    helperText: !serviceCompletedDate ? 'Enter completed date' : ''
                                                },
                                                field: {
                                                    clearable: true,
                                                    onClear: () => setCleared(true)
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <TextField size="small" fullWidth type='number'
                                            label="Service Completed Duty Days"
                                            value={serviceCompletedDutyDays}
                                            slotProps={{
                                                input: {
                                                    readOnly: true
                                                },
                                            }}
                                            error={serviceCompletedDutyDays <= 0 ? true : false}
                                            helperText={serviceCompletedDutyDays <= 0 ? 'Invalid completed duty days' : ''}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <TextField size="small" fullWidth type='number'
                                            label="Nurse Completed Duty Days"
                                            value={nurseCompletedDutyDays}
                                            slotProps={{
                                                input: {
                                                    readOnly: true
                                                },
                                            }}
                                            error={nurseCompletedDutyDays <= 0 ? true : false}
                                            helperText={nurseCompletedDutyDays <= 0 ? 'Invalid completed duty days' : ''}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <TextField size="small" fullWidth type='number'
                                            label="Extended Duty Days"
                                            value={serviceExtendedDutyDays}
                                            slotProps={{
                                                input: {
                                                    readOnly: true
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <TextField size="small" fullWidth
                                            label="Extended Duty Days Fee"
                                            value={serviceExtendedDutyDaysFee}
                                            error={serviceExtendedDutyDaysFee < 0 ? true : false}
                                            helperText={serviceExtendedDutyDaysFee < 0 ? 'Invalid fee amount' : ''}
                                            onChange={(event) => {
                                                setServiceExtendedDutyDaysFee(event.target.value);
                                            }}
                                            onBlur={(event) => {
                                                const tempFormValue = GetNoToCurrencyFormat(event.target.value ?? 0);
                                                setServiceExtendedDutyDaysFee(tempFormValue);
                                            }}
                                            onFocus={(event) => {
                                                if (serviceExtendedDutyDays > 0) {
                                                    const tempValue = parseFloat(event.target.value ?? 0);
                                                    if (tempValue > 0) {
                                                        setServiceExtendedDutyDaysFee(GetNumberFromCurrencyFormat(event.target.value));
                                                    } else {
                                                        setServiceExtendedDutyDaysFee('');
                                                    }
                                                }
                                            }}
                                            slotProps={{
                                                input: {
                                                    readOnly: serviceExtendedDutyDays <= 0
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <TextField size="small" fullWidth
                                            label="Extended Duty Nurse Salary"
                                            value={serviceExtDutyNurseSalary}
                                            error={serviceExtDutyNurseSalary < 0 ? true : false}
                                            helperText={serviceExtDutyNurseSalary < 0 ? 'Invalid salary amount' : ''}
                                            onChange={(event) => {
                                                setServiceExtDutyNurseSalary(event.target.value);
                                            }}
                                            onBlur={(event) => {
                                                const tempFormValue = GetNoToCurrencyFormat(event.target.value ?? 0);
                                                setServiceExtDutyNurseSalary(tempFormValue);
                                            }}
                                            onFocus={(event) => {
                                                if (serviceExtendedDutyDays > 0) {
                                                    const tempValue = parseFloat(event.target.value ?? 0);
                                                    if (tempValue > 0) {
                                                        setServiceExtDutyNurseSalary(GetNumberFromCurrencyFormat(event.target.value));
                                                    } else {
                                                        setServiceExtDutyNurseSalary('');
                                                    }
                                                }
                                            }}
                                            slotProps={{
                                                input: {
                                                    readOnly: serviceExtendedDutyDays <= 0
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField size="small" fullWidth
                                            label="Notes"
                                            helperText=""
                                            multiline
                                            rows={4}
                                            value={notes}
                                            onChange={(event) => {
                                                setNotes(event.target.value);
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        {
                            isLoading && <CircularProgress size="30px" />
                        }
                        <Button variant="contained" disabled={isLoading} onClick={handleSaveClick}>
                            Save
                        </Button>
                    </DialogActions>
                </BootstrapDialog> </LocalizationProvider>
        </React.Fragment>

    );
}

export default CompleteServiceForm;